/**
 * Ads Manager deep links to ads a blast created — mirror of the API's
 * `ads-manager-links.ts` so the Jobs page can link a row from the account
 * the job recorded (`adAccountId`: `act_…` for Meta, advertiser id for
 * TikTok) without a round-trip.
 */

export function metaAdsManagerLink(
  adAccountId: string,
  adIds: string[],
  businessId?: string,
): string {
  const act = adAccountId.replace(/^act_/, "")
  const ids = [...new Set(adIds.filter(Boolean))].slice(0, 50)
  const filterSet = `SEARCH_BY_ADGROUP_IDS-STRING_SET%1EANY%1E[${ids
    .map(id => `%22${id}%22`)
    .join(",")}]`
  const params = [
    `act=${act}`,
    ...(businessId ? [`business_id=${businessId}`] : []),
    `filter_set=${filterSet}`,
    `selected_ad_ids=${ids.join(",")}`,
    "nav_source=no_referrer",
  ]
  return `https://adsmanager.facebook.com/adsmanager/manage/ads?${params.join("&")}`
}

const isoDate = (d: Date): string => d.toISOString().slice(0, 10)
const DAY_MS = 24 * 60 * 60 * 1000

/**
 * TikTok Ads tab (`manage/creative`) filtered to the ad ids, in the
 * `filters[0]` shape Ads Manager writes itself. `st`/`et` is the reporting
 * window: last 7 days, opened back to the creation date when known.
 */
export function tiktokAdsManagerLink(
  advertiserId: string,
  adIds: string[],
  options: { createdAt?: Date | string | null; now?: Date } = {},
): string {
  const ids = [...new Set(adIds.filter(Boolean))].slice(0, 50)
  const now = options.now ?? new Date()
  let start = new Date(now.getTime() - 7 * DAY_MS)
  if (options.createdAt) {
    const created = new Date(options.createdAt)
    if (!Number.isNaN(created.getTime()) && created < start) start = created
  }
  const filter = (path: string, value: string) =>
    `filters%5B0%5D${path.replace(/\[/g, "%5B").replace(/\]/g, "%5D")}=${encodeURIComponent(value)}`
  const params = [
    `aadvid=${encodeURIComponent(advertiserId)}`,
    "workflow_card_type=0",
    `st=${isoDate(start)}`,
    `et=${isoDate(now)}`,
    filter("[field]", "creative_ids"),
    filter("[filter_type]", "0"),
    ...ids.map((id, i) => filter(`[in_field_values][${i}]`, id)),
  ]
  return `https://ads.tiktok.com/i18n/manage/creative?${params.join("&")}`
}

export function adsManagerLink(input: {
  platform?: string | null
  adAccountId?: string | null
  adIds: Array<string | undefined | null>
  businessId?: string | null
  /** When the ad(s) were created — widens TikTok's reporting window so the ad is in view. */
  createdAt?: Date | string | null
}): string | undefined {
  const ids = input.adIds.filter(
    (id): id is string => typeof id === "string" && id !== "",
  )
  if (ids.length === 0 || !input.adAccountId) return undefined
  switch ((input.platform ?? "meta").toLowerCase()) {
    case "meta":
      return metaAdsManagerLink(
        input.adAccountId,
        ids,
        input.businessId ?? undefined,
      )
    case "tiktok":
      return tiktokAdsManagerLink(input.adAccountId, ids, {
        createdAt: input.createdAt,
      })
    default:
      return undefined
  }
}
